import { FC, useCallback, useMemo } from 'react';
import { useAtom } from 'jotai';
import { Link } from 'components/link/link';
import { DataTable, type DataTableColumnDef } from 'components/data-table';
import type {
  InitialTableState,
  TableState,
} from 'components/data-table/types';
import {
  createExpenseScenariosSearchAtom,
  DEFAULT_PARAMS,
  SORT_FIELD_MAP,
  columnFiltersToParams,
} from './atoms';
import {
  AtomisedNotifications,
  NotificationGroup,
} from 'components/atomised-notifications';
import type { ExpenseScenarioResult } from './types';
import { ExpenseScenariosFilters } from './expense-scenarios-filters';
import { ExpenseScenariosSummaryCards } from './summary-cards';
import { CloneButton } from './clone-button';
import styles from './expense-scenarios-content.module.scss';

type ExpenseScenarioRow = ExpenseScenarioResult & Record<string, unknown>;

const DATA_COLUMNS: DataTableColumnDef<ExpenseScenarioRow>[] = [
  {
    accessorKey: 'name',
    header: 'Name',
    size: 280,
    cell: ({ row }) => <Link to={row.original.href}>{row.original.name}</Link>,
  },
  {
    accessorKey: 'description',
    header: 'Description',
    size: 360,
  },
  {
    accessorKey: 'areaCount',
    header: 'Areas',
    size: 100,
    meta: { type: 'integer' },
  },
  {
    accessorKey: 'opexItemCount',
    header: 'OpEx Items',
    size: 120,
    meta: { type: 'integer' },
  },
  {
    accessorKey: 'capexItemCount',
    header: 'CapEx Items',
    size: 120,
    meta: { type: 'integer' },
  },
  {
    accessorKey: 'effectiveDate',
    header: 'Effective Date',
    size: 140,
    meta: { type: 'date' },
  },
  {
    accessorKey: 'createdBy',
    header: 'Created By',
    size: 160,
    meta: { type: 'enum' },
  },
  {
    accessorKey: 'createdAt',
    header: 'Created At',
    size: 140,
    meta: { type: 'date' },
  },
  {
    accessorKey: 'updatedAt',
    header: 'Updated At',
    size: 140,
    meta: { type: 'date' },
  },
];

const initialTableState: InitialTableState = {
  columnPinning: { left: ['name'] },
  // Seed the column order explicitly so the id'd 'actions' column stays last
  // (the DataTable's default order derives ids from accessorKey-only columns as
  // empty strings, which would otherwise float 'actions' to the front).
  columnOrder: [
    'name',
    'description',
    'areaCount',
    'opexItemCount',
    'capexItemCount',
    'effectiveDate',
    'createdBy',
    'createdAt',
    'updatedAt',
    'actions',
  ],
  sortColumn: 'createdAt',
  sortDirection: 'desc',
  pageSize: 100,
};

const ExpenseScenariosContent: FC = () => {
  const { searchAtom } = useMemo(() => createExpenseScenariosSearchAtom(), []);
  const [searchResult, setSearchPayload] = useAtom(searchAtom);

  // The search atom's onMount fires the initial fetch (with DEFAULT_PARAMS), so
  // the list is driven purely through setSearchPayload — no separate params
  // state / mount effect, which would double-fire the initial request.

  // Re-fires the current search so a successful clone surfaces in the list.
  const refreshList = useCallback(
    () => setSearchPayload((current) => current && { ...current }),
    [setSearchPayload]
  );

  const columns = useMemo<DataTableColumnDef<ExpenseScenarioRow>[]>(
    () => [
      ...DATA_COLUMNS,
      {
        id: 'actions',
        header: '',
        // Wide enough for the whole "Clone Scenario" label. It was 60 — right
        // for the bare icon this used to be, and enough to clip the label to
        // "Cl" once the button gained one, which is worse than the icon was.
        size: 180,
        enableSorting: false,
        enableColumnFilter: false,
        enableHiding: false,
        cell: ({ row }) => (
          <CloneButton id={row.original.id} onCloned={refreshList} />
        ),
      },
    ],
    [refreshList]
  );

  const refetch = useCallback(
    ({ page, pageSize, sorting, globalFilter, columnFilters }: TableState) => {
      const sort = sorting[0];
      // Only BE-sortable columns (SORT_FIELD_MAP) drive the sort param; anything
      // else (counts, description, createdBy) falls back to the default sort so
      // the BE never receives an unknown sort_attribute (which 500s).
      const mappedSort = sort ? SORT_FIELD_MAP[sort.id] : undefined;
      setSearchPayload(() => ({
        ...DEFAULT_PARAMS,
        page,
        pageSize,
        sortAttribute: mappedSort ?? DEFAULT_PARAMS.sortAttribute,
        sortDirection: mappedSort
          ? sort?.desc === false
            ? 'ASC'
            : 'DESC'
          : DEFAULT_PARAMS.sortDirection,
        text: globalFilter || undefined,
        ...columnFiltersToParams(columnFilters),
      }));
    },
    [setSearchPayload]
  );

  const rows = (searchResult.data?.results ?? []) as ExpenseScenarioRow[];

  return (
    <div className={styles.contentWrapper}>
      <AtomisedNotifications groupIds={[NotificationGroup.ExpenseScenarios]} />
      <ExpenseScenariosSummaryCards
        aggregations={searchResult.data?.aggregations}
      />
      <div className={styles.tableWrapper}>
        <DataTable<ExpenseScenarioRow>
          data={rows}
          columns={columns}
          getRowId={(row) => String(row.id)}
          initialTableState={initialTableState}
          persistenceKey="expense-scenarios-list-table"
          config={{ columnFilters: false, columnReordering: false }}
          isLoading={searchResult.loading}
          error={searchResult.error ?? null}
          scroll={{
            mode: 'paginated',
            serverDriven: true,
            totalCount: searchResult.data?.totalCount ?? 0,
          }}
          onPaginationChange={refetch}
          onSortChange={refetch}
          onFilterChange={refetch}
          onGlobalSearchChange={refetch}
          searchPlaceholder="Search by name, creator, or description..."
          renderFilterPanel={() => (
            <ExpenseScenariosFilters facets={searchResult.data?.facets} />
          )}
        />
      </div>
    </div>
  );
};

export default ExpenseScenariosContent;
